import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { SchedulerCoreState, SchedulerTrack } from '@radioking/scheduler-core';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { BehaviorSubject, concat, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, delay, map, tap } from 'rxjs/operators';

import { Program, ProgramPlaylist } from '../../models/programs.model';
import {
  ProgramAddPlaylistSuccess,
  ProgramDuplicatePlaylistSuccess,
  ProgramPlaylistsFailure,
  ProgramPlaylistsRequest,
  ProgramPlaylistsSuccess,
} from '../../states/programs.actions';
import { PreviewProgramModalComponent } from '../modals/preview-program-modal/preview-program-modal.component';

@Component({
  selector: 'rk-program-details-list',
  templateUrl: './program-details-list.component.html',
  styleUrls: ['./program-details-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgramDetailsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() program: Program;

  @ViewChild('list')
  listEl: ElementRef<HTMLDivElement>;

  added: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  insertedIntoList: BehaviorSubject<ProgramPlaylist | null> =
    new BehaviorSubject<ProgramPlaylist | null>(null);
  trackIntro$: Observable<SchedulerTrack>;
  trackExtro$: Observable<SchedulerTrack>;
  isLoading$: Observable<boolean>;
  resetChildScrollAxis = new Subject<any>();

  subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly helper: UiStoreHelperService,
    private readonly dialog: MatDialog,
    private readonly actions$: Actions,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.getPlaylists(this.program.id);
    this.isLoading$ = this.helper.getLoadingState(
      [ProgramPlaylistsRequest],
      [ProgramPlaylistsSuccess, ProgramPlaylistsFailure],
      true,
    );

    this.subscription.add(
      concat(
        this.actions$.pipe(
          ofActionSuccessful(ProgramAddPlaylistSuccess, ProgramDuplicatePlaylistSuccess),
        ),
        this.actions$.pipe(ofActionSuccessful(ProgramPlaylistsSuccess)),
      )
        .pipe(
          debounceTime(600),
          tap(
            (
              success:
                | ProgramAddPlaylistSuccess
                | ProgramDuplicatePlaylistSuccess
                | ProgramPlaylistsSuccess,
            ) => {
              let scrollToEnd = true;

              if (!(success instanceof ProgramPlaylistsSuccess) && success.playlist) {
                this.insertedIntoList.next(success.playlist);
                scrollToEnd =
                  success.playlist.idProgramPlaylist ===
                  this.program.playlists[this.program.playlists.length - 1]
                    ?.idProgramPlaylist;
              } else {
                this.added.next(true);
              }

              if (scrollToEnd) {
                this.listEl.nativeElement.scrollTop =
                  45 * (this.program.playlistsNumber + 5);
              }

              this.cdr.detectChanges();
            },
          ),
          delay(2000),
          tap(() => {
            this.insertedIntoList.next(null);
            this.added.next(false);
          }),
        )
        .subscribe(),
    );
  }

  isNew(pl: ProgramPlaylist) {
    return (
      (pl.position === this.program.playlistsNumber && this.added.value) ||
      pl.idProgramPlaylist === this.insertedIntoList.value?.idProgramPlaylist
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.program &&
      !changes.program.firstChange &&
      changes.program.currentValue.id !== changes.program.previousValue.id
    ) {
      this.getPlaylists(this.program.id);
    }

    if (this.program.idTrackIntro) {
      this.trackIntro$ = this.store
        .select(SchedulerCoreState.trackWithId)
        .pipe(map(fn => fn(this.program.idTrackIntro)));
    } else {
      this.trackIntro$ = null;
    }
    if (this.program.idTrackExtro) {
      this.trackExtro$ = this.store
        .select(SchedulerCoreState.trackWithId)
        .pipe(map(fn => fn(this.program.idTrackExtro)));
    } else {
      this.trackExtro$ = null;
    }
  }

  onResetAllScrollAxis() {
    this.resetChildScrollAxis.next(Math.random());
  }

  @Dispatch()
  getPlaylists(id: number) {
    return new ProgramPlaylistsRequest(id);
  }

  previewProgram() {
    this.dialog.open(PreviewProgramModalComponent, { data: this.program });
  }

  formatSchedulerTrack(track: SchedulerTrack): string {
    return [track.title, track.artist].filter(v => v).join(' - ');
  }
}
